/**
 * Court layer: San Francisco Superior Court. See src/courts/index.js for the shape.
 * Every entry comes from a file in data/documents/ (named in `cite`).
 */
export default {
  id: "sf-superior",

  holidays: {
    // The court's published schedule, including the weekday it observes for a holiday on a weekend.
    observed: [
      "2026-01-01", "2026-01-19", "2026-02-12", "2026-02-16", "2026-03-31", "2026-05-25", "2026-06-19",
      "2026-07-03", "2026-09-07", "2026-09-25", "2026-11-11", "2026-11-26", "2026-11-27", "2026-12-25",
      "2027-01-01", "2027-01-18", "2027-02-12", "2027-02-15", "2027-03-31", "2027-05-31", "2027-06-18",
      "2027-07-05", "2027-09-06", "2027-09-24", "2027-11-11", "2027-11-25", "2027-11-26", "2027-12-24",
      "2027-12-31",
    ],
    closures: [],
    through: "2027-12-31",
    cite: "SF Court Holiday Schedules 2026 and 2027",
  },

  details: {
    fees: {
      firstPaper: {
        // GC 70625 local courthouse construction surcharge; limited civil unchanged.
        value: { unlimited: 450, limitedOver10k: 370, limitedUpTo10k: 225, udLimitedOver10k: 385, udLimitedUpTo10k: 240 },
        text: "San Francisco adds a courthouse construction surcharge to first papers in unlimited cases, so the unlimited first paper fee is $450. Limited civil fees are the statewide amounts.",
        cite: "Statewide Civil Fee Schedule, Appendix; GC 70625",
      },
    },

    departments: {
      caseManagement: {
        value: "610",
        text: "Civil Case Management, Dept. 610: case management conferences on Wednesdays at 10:30 a.m.; orders to show cause (including dismissal under CRC 3.1385) on Tuesdays at 10:30 a.m., in person only.",
        cite: "LR 3.0(A); SF Civil Case Management page",
      },
      lawAndMotion: {
        value: { odd: "301", even: "302" },
        text: "Law & Motion, Dept. 301 for odd case numbers and Dept. 302 for even case numbers, daily at 9:00 a.m. Remote appearance by Zoom is allowed.",
        cite: "SF Law & Motion & Discovery page",
      },
      discovery: {
        value: { odd: "301", even: "302" },
        text: "Discovery Calendar, Dept. 301 for odd case numbers and Dept. 302 for even case numbers, daily at 9:00 a.m.",
        cite: "LR 4.1(C); SF Law & Motion & Discovery page",
      },
      exParte: {
        value: { odd: "301", even: "302" },
        text: "Ex parte hearings in Dept. 301 (odd) or 302 (even), daily at 11:00 a.m., by CourtCall only. E-file the moving papers and deliver a copy to the department at least 2 hours before the hearing.",
        cite: "LR 9.0(A); SF Law & Motion & Discovery page",
      },
      unlawfulDetainer: {
        value: "501",
        text: "Real Property/Housing Court, Dept. 501: every request in an unlawful detainer case, and all law and motion and ex parte requests in a real property case.",
        cite: "LR 8.10; SF Civil Case Management page, table of departments",
      },
      presidingJudge: {
        value: "206",
        text: "Dept. 206 (Presiding Judge): trial continuances, service on a corporation through the Secretary of State, service of summons by publication or by another court-authorized method, guardians ad litem and fee waivers.",
        cite: "LR 3.1(C); SF Civil Case Management page, table of departments",
      },
      asbestosCeqa: {
        value: "304",
        text: "Dept. 304: every request in asbestos and CEQA cases.",
        cite: "SF Civil Case Management page, table of departments",
      },
    },

    rules: {
      courtesyCopies: {
        text: "Lodge paper courtesy copies, tabbed and bound, of any filed document that needs court review, action or signature, in the department that will hear it: at filing for paper filings, the same day for papers filed fewer than 7 court days before the hearing, otherwise within 2 court days after e-filing. Include the e-filing Transaction Receipt.",
        cite: "LR 2.7(B)",
      },
      cmcNotice: {
        text: "When the complaint is filed, the clerk issues a notice to the plaintiff with the case management conference date. The plaintiff must serve it on every defendant.",
        cite: "LR 3.2(A)",
      },
      cmStatement: {
        value: { lodgeDaysBefore: 25 },
        text: "Dept. 610 reviews the statements before the conference and may issue a case management order without one. It asks that the CM-110 be filed, served and lodged 25 days before the conference to make that possible, and that courtesy copies reach it by 1:30 p.m. the day after e-filing.",
        cite: "LR 3.2(B); SF Civil Case Management page",
      },
      cmcContinuance: {
        text: "Ask to continue a case management conference in the case management statement, or in a supplemental statement if the statement is already filed.",
        cite: "LR 3.2(D)",
      },
      cmcRemote: {
        text: "Case management conferences and objection hearings in Dept. 610 may be attended remotely by Zoom. Orders to show cause may not.",
        cite: "LR 3.2(E); SF Civil Case Management page",
      },
      extensionToServe: {
        text: "To extend time to serve the summons and complaint, file a written application and deliver a courtesy copy with a proposed order to Dept. 610.",
        cite: "LR 3.1(A)",
      },
      extensionToRespond: {
        text: "To extend time to respond, file a written application. Opposition is due within 2 court days of service of the request. Lodge courtesy copies of both, with a proposed order, in Dept. 610.",
        cite: "LR 3.1(B)",
      },
      serviceOrders: {
        text: "Applications for leave to serve by publication, through the Secretary of State or by another method needing court approval go to the Presiding Judge. Posting in an unlawful detainer goes to the Real Property Court.",
        cite: "LR 3.1(C)",
      },
      judgeProTem: {
        text: "A party is deemed to agree that matters in Dept. 610 may be heard by a temporary judge unless it files a written objection within 30 days after its first pleading (or at the first hearing in Dept. 610, if sooner).",
        cite: "LR 3.3(A)",
      },
      adrPackage: {
        text: "The plaintiff must serve the court's ADR Information Package on each defendant with the complaint. A cross-complainant serves it on any new party with the cross-complaint.",
        cite: "LR 4.0; CRC 3.221(c); SF ADR Information Package",
        forms: ["ca-sf-adr-1"],
      },
      adrStipulation: {
        text: "If all parties agree to ADR, file the court's Stipulation to Alternative Dispute Resolution (Dept. 610).",
        cite: "SF Civil Case Management page; CRC 3.221",
        forms: ["ca-sf-civ-022"],
      },
      uninsuredMotorist: {
        text: "In an uninsured motorist case, the plaintiff files an ex parte application in Dept. 610, at filing or within 10 days of learning it is one, to have it designated as such.",
        cite: "LR 3.0(B)",
      },
      complexDesignation: {
        text: "Checking \"complex\" on the CM-010 is not enough: file an application for complex designation and pay the complex fee (GC 70616(a)) when you apply.",
        cite: "LR 3.5(C),(D)",
      },
      tentativeRulings: {
        text: "Law and motion tentative rulings are generally available by 3:00 p.m. the day before the hearing. To contest one, email the department's contest address, copying all other parties, by 4:00 p.m. the day before. A party that does not appear submits to the tentative ruling.",
        cite: "LR 8.3(C),(D),(F)",
      },
      oscPapers: {
        text: "Lodge courtesy copies of any papers for an order to show cause in Dept. 610 the same day they are filed. Dept. 610 asks that responsive papers be filed, served and lodged 20 days before the hearing so it can rule without an appearance.",
        cite: "SF Civil Case Management page",
      },
      settlementRetention: {
        text: "San Francisco's Stipulation and [Proposed] Order Re: Settlement and Retention of Jurisdiction (SFCIV-021) asks the court to retain jurisdiction under CCP 664.6 after dismissal. It is signed by the plaintiff, the defendant and their attorneys, and must be filed before the dismissal is entered.",
        cite: "SF Form SFCIV-021; SF Local Forms page",
        forms: ["ca-sf-civ-021"],
      },
      settlementBeforeTrial: {
        text: "If a trial date is set, the parties must appear on the day of trial unless a notice of dismissal, settlement or stay is filed with courtesy copies delivered to Dept. 206 by 4 p.m. on the Thursday before trial (or before a continued trial date). Missing it may lead to sanctions under CCP 177.5.",
        cite: "SF Presiding Judge / Master Calendar page",
      },
      arbitrationSettled: {
        text: "If the case is in judicial arbitration, give the Arbitration Administrator written notice that it has settled or been dismissed, so it is dropped from the arbitration OSC calendar.",
        cite: "LR 4.1(F)",
      },
      noFax: {
        text: "The court does not accept fax filings directly. Fax-produced papers must be filed at the window or by mail.",
        cite: "LR 2.7(C)",
      },
    },

    forms: {
      doeAmendment: {
        text: "To name a Doe defendant or correct a defendant's name later, San Francisco has local forms: Amendment - Fictitious Name (SFCIV-014, with attachment SFCIV-015) and Amendment - Incorrect Name (SFCIV-016). Pay any local fee electronically, then deliver a paper copy to the Law & Motion department for review.",
        cite: "SF Local Forms; SF Law & Motion & Discovery page",
        forms: ["ca-sf-civ-014", "ca-sf-civ-015", "ca-sf-civ-016"],
      },
    },

    efiling: {
      represented: {
        value: true,
        text: "E-filing through an approved vendor is mandatory for represented parties in every general civil case (a Designated Case). File everything for one pleading or motion in a single transaction. Fees for e-filed papers are paid through the vendor.",
        cite: "LR 2.11(A)(3), (C)(1), (I), (P)",
      },
      selfRepresented: {
        text: "Self-represented parties and non-parties are excluded from mandatory e-filing and e-service, and must be served on paper unless they agree to e-service.",
        cite: "LR 2.11(D)(2)",
      },
      conventionalInitiating: {
        text: "The initiating papers in an unlawful detainer (and some other case types) must be filed on paper, as must unlawful detainer summonses, writs and abstracts.",
        cite: "LR 2.11(D)(3)-(4)",
      },
      documentTitle: {
        text: "Title the e-filed document with the filing party, the kind of document, the party against whom relief is sought and the relief, e.g. \"Defendant ABC Corporation's Request for Dismissal of Plaintiff John Smith's Complaint.\" The title entered with the vendor must match the caption.",
        cite: "SF E-Filing Special Instructions page",
      },
      firstEfiling: {
        text: "Give the party's e-service address the first time it e-files a paper. Each party has one e-service address per case.",
        cite: "LR 2.11(L)",
      },
    },

    hearingReservation: {
      lawAndMotion: {
        value: false,
        text: "No reservation: confer with the other parties, pick a date, then e-file and notice the hearing under CCP 1005. The date does not need to be cleared with Dept. 301/302.",
        cite: "SF Law & Motion & Discovery page",
      },
      discovery: {
        value: false,
        text: "No reservation: confer with the other parties, pick a date on the 9:00 a.m. Discovery Calendar, then e-file and notice it under CCP 1005.",
        cite: "LR 10.0(C); SF Law & Motion & Discovery page",
      },
    },
  },
};
